import { z } from 'zod'

import { resolveOnboardingApiBaseUrl } from './onboarding-api-endpoint'
import { resolveUserManagementBaseUrl } from './user-management-endpoint'

const environmentSchema = z
  .object({
    VITE_APPLICATION_ENVIRONMENT: z.enum(['dev', 'stage', 'prod']),
    VITE_FIREBASE_API_KEY: z.string().trim().min(1),
    VITE_FIREBASE_AUTH_DOMAIN: z.string().trim().min(1),
    VITE_FIREBASE_DATABASE_URL: z.string().url(),
    VITE_FIREBASE_PROJECT_ID: z.string().trim().min(1),
    VITE_FIREBASE_STORAGE_BUCKET: z.string().trim().min(1),
    VITE_USER_MANAGEMENT_BASE_URL: z.string().url(),
    VITE_ONBOARDING_API_BASE_URL: z.string().url(),
    VITE_ONBOARDING_SIGNUP_ENABLED: z
      .enum(['disabled', 'enabled'])
      .default('disabled'),
    VITE_ONBOARDING_GATE_ENABLED: z
      .enum(['disabled', 'enabled'])
      .default('disabled'),
  })
  .superRefine((value, context) => {
    const projectId = value.VITE_FIREBASE_PROJECT_ID.toLowerCase()
    const isStageProject = projectId.includes('stage')

    if (value.VITE_APPLICATION_ENVIRONMENT === 'stage' && !isStageProject) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['VITE_FIREBASE_PROJECT_ID'],
        message: 'Stage must use a Stage Firebase project',
      })
    }

    if (value.VITE_APPLICATION_ENVIRONMENT === 'prod' && isStageProject) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['VITE_FIREBASE_PROJECT_ID'],
        message: 'Production cannot use a Stage Firebase project',
      })
    }

    const relatedHosts = [
      ['VITE_FIREBASE_AUTH_DOMAIN', value.VITE_FIREBASE_AUTH_DOMAIN],
      ['VITE_FIREBASE_DATABASE_URL', value.VITE_FIREBASE_DATABASE_URL],
      ['VITE_FIREBASE_STORAGE_BUCKET', value.VITE_FIREBASE_STORAGE_BUCKET],
    ] as const

    for (const [field, configuredValue] of relatedHosts) {
      if (!configuredValue.toLowerCase().includes(projectId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: `${field} must identify the configured Firebase project`,
        })
      }
    }

    if (
      value.VITE_APPLICATION_ENVIRONMENT !== 'dev' &&
      !value.VITE_USER_MANAGEMENT_BASE_URL.startsWith('https://')
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['VITE_USER_MANAGEMENT_BASE_URL'],
        message: 'Stage and Production API URLs must use HTTPS',
      })
    }

    const expectedOnboardingUrl =
      `https://us-central1-${value.VITE_FIREBASE_PROJECT_ID}` +
      '.cloudfunctions.net/onboarding-api'
    if (value.VITE_ONBOARDING_API_BASE_URL !== expectedOnboardingUrl) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['VITE_ONBOARDING_API_BASE_URL'],
        message:
          'Onboarding API URL must identify the configured Firebase project',
      })
    }
  })

export type DashboardEnvironment = {
  applicationEnvironment: 'dev' | 'stage' | 'prod'
  firebase: {
    apiKey: string
    authDomain: string
    databaseURL: string
    projectId: string
    storageBucket: string
  }
  userManagementBaseUrl: string
  onboardingApiBaseUrl: string
  onboardingSignupEnabled: boolean
  onboardingGateEnabled: boolean
}

export function parseEnvironment(
  source: Record<string, unknown>
): DashboardEnvironment {
  const parsed = environmentSchema.parse(source)

  return {
    applicationEnvironment: parsed.VITE_APPLICATION_ENVIRONMENT,
    firebase: {
      apiKey: parsed.VITE_FIREBASE_API_KEY,
      authDomain: parsed.VITE_FIREBASE_AUTH_DOMAIN,
      databaseURL: parsed.VITE_FIREBASE_DATABASE_URL,
      projectId: parsed.VITE_FIREBASE_PROJECT_ID,
      storageBucket: parsed.VITE_FIREBASE_STORAGE_BUCKET,
    },
    userManagementBaseUrl: parsed.VITE_USER_MANAGEMENT_BASE_URL.replace(
      /\/$/,
      ''
    ),
    onboardingApiBaseUrl: parsed.VITE_ONBOARDING_API_BASE_URL.replace(
      /\/$/,
      ''
    ),
    onboardingSignupEnabled:
      parsed.VITE_ONBOARDING_SIGNUP_ENABLED === 'enabled',
    onboardingGateEnabled: parsed.VITE_ONBOARDING_GATE_ENABLED === 'enabled',
  }
}

let cachedEnvironment: DashboardEnvironment | undefined

export function getEnvironment(): DashboardEnvironment {
  if (!cachedEnvironment) {
    const parsedEnvironment = parseEnvironment({
      VITE_APPLICATION_ENVIRONMENT: import.meta.env
        .VITE_APPLICATION_ENVIRONMENT,
      VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
      VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      VITE_FIREBASE_DATABASE_URL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
      VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      VITE_FIREBASE_STORAGE_BUCKET: import.meta.env
        .VITE_FIREBASE_STORAGE_BUCKET,
      VITE_USER_MANAGEMENT_BASE_URL: import.meta.env
        .VITE_USER_MANAGEMENT_BASE_URL,
      VITE_ONBOARDING_API_BASE_URL: import.meta.env
        .VITE_ONBOARDING_API_BASE_URL,
      VITE_ONBOARDING_SIGNUP_ENABLED: import.meta.env
        .VITE_ONBOARDING_SIGNUP_ENABLED,
      VITE_ONBOARDING_GATE_ENABLED: import.meta.env
        .VITE_ONBOARDING_GATE_ENABLED,
    })
    cachedEnvironment = {
      ...parsedEnvironment,
      userManagementBaseUrl: import.meta.env.DEV
        ? resolveUserManagementBaseUrl(
            parsedEnvironment.userManagementBaseUrl,
            true
          )
        : parsedEnvironment.userManagementBaseUrl,
      onboardingApiBaseUrl: import.meta.env.DEV
        ? resolveOnboardingApiBaseUrl(
            parsedEnvironment.onboardingApiBaseUrl,
            true
          )
        : parsedEnvironment.onboardingApiBaseUrl,
    }
  }
  return cachedEnvironment
}
